export type FontOption = {
  label: string;
  value: string;
};

export type FontFaceSpec = {
  /** Primary CSS font-family name */
  family: string;
  /** File name under `src/fonts` (bundled via `index.css`; keep in sync with `/public`) */
  file: string;
  aliases?: string[];
};

/** Bundled OTF files in `/public` — single source for @font-face and editor options. */
export const PUBLIC_FONT_FACES: FontFaceSpec[] = [
  { family: 'Vag', file: 'Vag-Regular.otf', aliases: ['vag-regular', 'vag'] },
  { family: 'Vag-Bold', file: 'Vag-Bold.otf', aliases: ['vag-bold'] },
  { family: 'Vag-Italic', file: 'Vag-Italic.otf', aliases: ['vag-italic'] },
  { family: 'Vag-BoldItalic', file: 'Vag-BoldItalic.otf', aliases: ['vag-bolditalic'] },
  { family: 'knswami', file: 'knswami-Regular.otf', aliases: ['knswami-regular'] },
  { family: 'knswami-bold', file: 'knswami-Bold.otf' },
];

const DEVANAGARI_FALLBACK = '"Kohinoor Devanagari","Noto Sans Devanagari","Mukta",sans-serif';

/** Stored editor / override token (case-insensitive) → CSS font-family stack. */
const FONT_STACK_BY_KEY: Record<string, string> = {
  vag: `"Vag","vag-regular","vag",${DEVANAGARI_FALLBACK}`,
  'vag-regular': `"vag-regular","Vag","vag",${DEVANAGARI_FALLBACK}`,
  'vag-bold': `"Vag-Bold","vag-bold",${DEVANAGARI_FALLBACK}`,
  'vag-italic': `"Vag-Italic","vag-italic",${DEVANAGARI_FALLBACK}`,
  'vag-bolditalic': `"Vag-BoldItalic","vag-bolditalic",${DEVANAGARI_FALLBACK}`,
  knswami: `"knswami","knswami-regular",${DEVANAGARI_FALLBACK}`,
  'knswami-regular': `"knswami-regular","knswami",${DEVANAGARI_FALLBACK}`,
  'knswami-bold': `"knswami-bold","knswami",${DEVANAGARI_FALLBACK}`,
  serif: 'Georgia, "Times New Roman", serif',
  'sans-serif': 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  sans: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  monospace: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
  'kohinoor devanagari': `"Kohinoor Devanagari","Noto Sans Devanagari","Mukta",sans-serif`,
  'noto sans devanagari': `"Noto Sans Devanagari","Mukta",sans-serif`,
  mukta: `"Mukta","Noto Sans Devanagari",sans-serif`,
};

/** Regular face tokens that swap to a dedicated bold font file via the bold toggle. */
const DEDICATED_BOLD_REGULAR_KEYS = new Set(['vag', 'vag-regular', 'knswami', 'knswami-regular']);
const DEDICATED_BOLD_FACE_KEYS = new Set(['vag-bold', 'knswami-bold']);

/** Tokens whose weight is carried by the font file (bold toggle swaps family, not fontWeight). */
const DEDICATED_WEIGHT_FACE_KEYS = new Set<string>([
  'vag',
  'vag-regular',
  'vag-bold',
  'knswami',
  'knswami-regular',
  'knswami-bold',
]);

export const FONT_FAMILY_OPTIONS: FontOption[] = [
  { label: 'Default', value: '' },
  { label: 'Vag', value: 'Vag' },
  { label: 'Vag Regular', value: 'vag-regular' },
  { label: 'Vag Bold', value: 'Vag-Bold' },
  { label: 'Vag Italic', value: 'Vag-Italic' },
  { label: 'Vag Bold Italic', value: 'Vag-BoldItalic' },
  { label: 'KNSwami', value: 'knswami' },
  { label: 'KNSwami Regular', value: 'knswami-regular' },
  { label: 'KNSwami Bold', value: 'knswami-bold' },
  { label: 'Kohinoor Devanagari', value: 'Kohinoor Devanagari' },
  { label: 'Noto Sans Devanagari', value: 'Noto Sans Devanagari' },
  { label: 'Mukta', value: 'Mukta' },
  { label: 'Serif', value: 'serif' },
  { label: 'Sans', value: 'sans-serif' },
  { label: 'Monospace', value: 'monospace' },
];

export function normalizeFontToken(ff: unknown): string {
  return typeof ff === 'string' ? ff.trim().toLowerCase() : '';
}

export function resolveFontStack(ff?: string): string | undefined {
  const raw = typeof ff === 'string' ? ff.trim() : '';
  if (!raw) return undefined;
  return FONT_STACK_BY_KEY[normalizeFontToken(raw)] ?? raw;
}

export function isDedicatedBoldRegularFamily(ff: unknown): boolean {
  return DEDICATED_BOLD_REGULAR_KEYS.has(normalizeFontToken(ff));
}

export function isDedicatedBoldFaceFamily(ff: unknown): boolean {
  return DEDICATED_BOLD_FACE_KEYS.has(normalizeFontToken(ff));
}

export function usesDedicatedFontFileForWeight(ff: unknown): boolean {
  return DEDICATED_WEIGHT_FACE_KEYS.has(normalizeFontToken(ff));
}

export function isBoldFromFontFamily(ff: unknown): boolean | undefined {
  if (isDedicatedBoldFaceFamily(ff)) return true;
  if (isDedicatedBoldRegularFamily(ff)) return false;
  return undefined;
}

const BOLD_TOGGLE_TARGETS: Record<string, { on: string; off: string }> = {
  vag: { on: 'Vag-Bold', off: 'Vag' },
  'vag-regular': { on: 'Vag-Bold', off: 'vag-regular' },
  'vag-bold': { on: 'Vag-Bold', off: 'Vag' },
  knswami: { on: 'knswami-bold', off: 'knswami' },
  'knswami-regular': { on: 'knswami-bold', off: 'knswami-regular' },
  'knswami-bold': { on: 'knswami-bold', off: 'knswami' },
};

/** Swap regular ↔ dedicated bold font files when toggling bold in the editor. */
export function applyDedicatedBoldToggleToFontFamily(
  currentFontFamily: unknown,
  boldOn: boolean,
): { fontFamily: string; handled: true } | { handled: false } {
  const key = normalizeFontToken(currentFontFamily);
  const pair = BOLD_TOGGLE_TARGETS[key];
  if (!pair) return { handled: false };
  return { fontFamily: boldOn ? pair.on : pair.off, handled: true };
}
